import Badge from '@mui/joy/Badge'
import { useAppSelector } from 'appState/store'
import { apiPaths } from 'configs/apiConfigs'
import { ClaimsEOBStatusEnum, IClaim } from 'features/claims/types'
import { useAppealEnumDescriptions } from 'features/claims/hooks/useAppealEnumDescriptions'
import { useEffect, useState } from 'react'
import { CardTabs } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { httpClaims } from 'utils/http'

import { mapFilteredSlotObjects } from 'utils/dataExtractor'
import { ClaimDetailsExtracted } from './ClaimDetailsExtracted'
import { ClaimsEOB } from './ClaimEOB/ClaimsEOB'
import { ClaimLineItems } from './ClaimLineItems/ClaimLineItems'
import { EntityTemplateAppealInfo, EntityTemplateInfo, LINEITEM_TEMPLATE } from './Constants'
import ClaimsAppeal from './ClaimsAppeal'

export interface IClaimDetailsPayerProps {
  claim: IClaim
  slotData: any
}

export const CallInsight = ({ claim, slotData }: IClaimDetailsPayerProps) => {
  const [EOBStatus, SetEOBStatus] = useState(null)
  const [EOBRequestTime, SetEOBRquestTime] = useState(null)
  const [anyEobReceived, SetAnyEobReceived] = useState(false)
  const [serviceLineItemsStatus, SetServiceLineItemsStatus] = useState([])
  const [EOBReceived, SetEOBReceived] = useState([])
  const appealEnumDescriptions = useAppealEnumDescriptions()
  const ascEnabled = useAppSelector((state) => state.auth.ascEnabled)
  const filteredExtractionData = mapFilteredSlotObjects(slotData, EntityTemplateInfo)
  const filteredCallInsightData = mapFilteredSlotObjects(slotData, LINEITEM_TEMPLATE)
  const filteredAppealInfo = mapFilteredSlotObjects(slotData, EntityTemplateAppealInfo, appealEnumDescriptions)

  useEffect(() => {
    if (claim) {
      httpClaims(apiPaths.claimEOBRequest(claim.oaiClaimId)).then((res) => {
        if (res.data) {
          SetEOBStatus(res.data.currentEOBStatus)
          SetEOBRquestTime(res.data.lastCallCompletionTime)
          SetAnyEobReceived(res.data.anyEOBReceived)
        }
      })
      httpClaims(apiPaths.serviceLineItems(claim.oaiClaimId)).then((res) => {
        if (res.data) {
          SetServiceLineItemsStatus(res.data.lineItems)
        }
      })
      httpClaims(apiPaths.claimEOBReceived(claim.oaiClaimId)).then((res) => {
        if (res.data) {
          const filteredRecords = res.data.filter((eob: any) => eob.fileName !== '')
          SetEOBReceived(filteredRecords)
        }
      })
    }
  }, [claim, SetEOBStatus, SetEOBReceived, SetServiceLineItemsStatus])

  let claimsCount = 0

  if (filteredExtractionData) {
    claimsCount = filteredExtractionData.filter((entity) => entity.value !== '---').length
  }

  return (
    <Div>
      <CardTabs
        title="Payer Call Results"
        tabSelectedIndex={serviceLineItemsStatus.length > 0 ? 1 : 0}
        tabs={[
          {
            tabs: (
              <>
                Claims{' '}
                {claimsCount > 0 && (
                  <span className="badge-holder">
                    <Badge badgeInset="5%" size="sm" color="success"></Badge>
                  </span>
                )}
              </>
            ),
            tabContent: <ClaimDetailsExtracted data={filteredExtractionData} claim={claim} />,
          },
          {
            tabs: (
              <>
                Service Line-Items{' '}
                {serviceLineItemsStatus.length > 0 && (
                  <span className="badge-holder">
                    <Badge badgeInset="5%" size="sm" color="success"></Badge>
                  </span>
                )}
              </>
            ),
            tabContent: <ClaimLineItems claim={claim} data={filteredCallInsightData} />,
          },
          {
            tabs: (
              <>
                EOB{' '}
                {
                  <span className="badge-holder">
                    {(EOBStatus !== ClaimsEOBStatusEnum.notRequested || anyEobReceived) && (
                      <Badge
                        badgeInset="5%"
                        size="sm"
                        color={
                          anyEobReceived && EOBReceived.length > 0
                            ? 'success'
                            : EOBStatus === ClaimsEOBStatusEnum.requested // Convert enumeration to string
                            ? 'neutral'
                            : EOBStatus === ClaimsEOBStatusEnum.requestedNotReceivedOnTime // Convert enumeration to string
                            ? 'danger'
                            : 'neutral'
                        }
                      ></Badge>
                    )}
                  </span>
                }
              </>
            ),
            tabContent: (
              <ClaimsEOB
                claim={claim}
                anyEobReceived={anyEobReceived}
                ClaimEOBStatus={EOBStatus!}
                EOBRequest={EOBRequestTime!}
              />
            ),
          },
          // Appeal tab is only shown for customers when it is enabled
          ...(ascEnabled ? [{ tabs: 'Appeal', tabContent: <ClaimsAppeal data={filteredAppealInfo} /> }] : []),

          // {
          //   tabs: (
          //     <>
          //       Eligibility & Benefits{' '}
          //       {ebvCount > 0 && (
          //         <span className="badge-holder">
          //           <Badge badgeInset="5%" size="sm" color="success"></Badge>
          //         </span>
          //       )}
          //     </>
          //   ),
          //   tabContent: <ClaimsEBV claim={claim} type={CoverageStatus} entityDictionary={entityDictionary} />,
          // },
        ]}
        action={<> {mapFilteredSlotObjects(slotData, ['insurer_name'])[0]?.value ?? ''} </>}
      />
    </Div>
  )
}

const Div = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  .badge-holder {
    margin-left: 10px;
  }

  .circle {
    height: 7px;
    width: 7px;
    background-color: ${({ theme }) => theme.colors.success.light};
    border-radius: 50%;
    display: inline-block;
    margin-left: 3px;
  }
`
